<script setup lang="ts">
// ATTENTION: этот компонент был вытащен из проекта magnit.ru, нет гарантий что он 100% соответствует дизайн системе
// Сейчас он внесён, чтобы компонент range из unit-filters мог его переиспользовать
import { watch, ref, onMounted, onBeforeUnmount, computed } from "vue";
import { throttledWatch, useMouse } from "@vueuse/core";

const RANGE_THUMBS_PADDING_PX = 14;
const MOUSE_MOVE_THROTTLING_MS = 25;

interface IRangeInputValue {
  min: number;
  max: number;
}

interface IRangeInputProps {
  min: number;
  max: number;
  currentMin: number;
  currentMax: number;
  step: number;
}

const props = defineProps<IRangeInputProps>();

const emit = defineEmits<{
  change: [IRangeInputValue];
  drag: [IRangeInputValue];
}>();
defineOptions({ inheritAttrs: false });

const { x: cursorX } = useMouse();

const localMin = ref(0);
const localMax = ref(0);
const inputContainerRef = ref<HTMLDivElement | null>(null);
const currentActiveThumb = ref<string | null>(null);
const currentSelectedThumb = ref<string | null>(null);
const currentTrackPixelWidth = ref(1);
const currentTrackLeftX = ref(0);
const minTrackPositionOffset = ref(0);
const maxTrackPositionOffset = ref(100);
const minThumbLeftCss = computed(
  () => `calc(${minTrackPositionOffset.value}% - ${RANGE_THUMBS_PADDING_PX}px)`,
);
const maxThumbLeftCss = computed(
  () => `calc(${maxTrackPositionOffset.value}% - ${RANGE_THUMBS_PADDING_PX}px)`,
);
const maxThumbRightCss = computed(
  () => `calc(100% - ${maxThumbLeftCss.value})`,
);

const changeRangeValue = () => {
  const valueDelta =
    (cursorX.value - currentTrackLeftX.value) / currentTrackPixelWidth.value;
  const percentValue =
    (valueDelta > 1 ? 1 : valueDelta < 0 ? 0 : valueDelta) * 100;
  const roundedToStepValue =
    props.min +
    Math.floor(((props.max - props.min) * percentValue) / 100 / props.step) *
      props.step;
  if (currentActiveThumb.value === "min") {
    if (percentValue > maxTrackPositionOffset.value) {
      minTrackPositionOffset.value = maxTrackPositionOffset.value;
      localMin.value = localMax.value;
    } else if (percentValue === 0) {
      localMin.value = props.min;
    } else {
      minTrackPositionOffset.value = percentValue;
      localMin.value = roundedToStepValue;
    }
  } else if (currentActiveThumb.value === "max") {
    if (percentValue < minTrackPositionOffset.value) {
      maxTrackPositionOffset.value = minTrackPositionOffset.value;
      localMax.value = localMin.value;
    } else if (percentValue > 100) {
      maxTrackPositionOffset.value = 100;
      localMax.value = props.max;
    } else {
      maxTrackPositionOffset.value = percentValue;
      localMax.value = roundedToStepValue;
    }
  }
  emit("drag", { min: localMin.value, max: localMax.value });
};

const onStartMovingRangeThumb = (thumb: "min" | "max") => {
  const element = inputContainerRef.value && inputContainerRef.value;
  if (element) {
    const { left, width } = element.getBoundingClientRect();
    currentTrackLeftX.value = left;
    currentTrackPixelWidth.value = width;
  }
  currentSelectedThumb.value = thumb;
  currentActiveThumb.value = thumb;
};

const onStopMovingRangeThumb = () => {
  if (currentSelectedThumb.value) {
    emit("change", { min: localMin.value, max: localMax.value });
  }
  currentActiveThumb.value = null;
};

throttledWatch(
  cursorX,
  () => {
    if (currentActiveThumb.value) {
      changeRangeValue();
    }
  },
  { throttle: MOUSE_MOVE_THROTTLING_MS },
);

watch(
  () => [props.currentMin, props.currentMax],
  (next) => {
    if (!currentActiveThumb.value) {
      if (next[0] && localMin.value !== next[0]) {
        const newMin =
          next[0] < props.min
            ? props.min
            : next[0] > next[1]
              ? next[1]
              : next[0];
        localMin.value = newMin;
        minTrackPositionOffset.value =
          ((newMin - props.min) / (props.max - props.min)) * 100;
        currentSelectedThumb.value = "min";
      }
      if (next[1] && localMax.value !== next[1]) {
        const newMax =
          next[1] > props.max
            ? props.max
            : next[1] < next[0]
              ? next[0]
              : next[1];
        localMax.value = newMax;
        maxTrackPositionOffset.value =
          ((newMax - props.min) / (props.max - props.min)) * 100;
        currentSelectedThumb.value = "max";
      }
    }
  },
  { immediate: true },
);

watch(
  () => [props.min, props.max],
  (next) => {
    localMin.value = next[0];
    localMax.value = next[1];
    minTrackPositionOffset.value = 0;
    maxTrackPositionOffset.value = 100;
  },
);

onMounted(() => {
  document.addEventListener("pointerup", onStopMovingRangeThumb);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerup", onStopMovingRangeThumb);
});
</script>

<template>
  <div class="pl-range-slider">
    <div ref="inputContainerRef" class="pl-range-slider__inner-wrapper">
      <label
        class="pl-range-slider__range-item"
        :class="{
          'pl-range-slider__range-item_active': currentSelectedThumb === 'min',
        }"
      >
        <input
          v-model="localMin"
          :step="step"
          :min="min"
          :max="max"
          type="range"
        />
        <span
          class="pl-range-slider__thumb"
          :style="{ left: minThumbLeftCss }"
          @pointerdown="onStartMovingRangeThumb('min')"
        />
        <span
          class="pl-range-slider__thumb-track pl-range-slider__thumb-track_min"
          :style="{ width: minThumbLeftCss }"
        />
      </label>
      <label
        class="pl-range-slider__range-item"
        :class="{
          'pl-range-slider__range-item_active': currentSelectedThumb === 'max',
        }"
      >
        <input
          v-model="localMax"
          :step="step"
          :min="min"
          :max="max"
          type="range"
        />
        <span
          class="pl-range-slider__thumb pl-range-slider__thumb_max"
          :style="{ left: maxThumbLeftCss }"
          @pointerdown="onStartMovingRangeThumb('max')"
        />
        <span
          class="pl-range-slider__thumb-track pl-range-slider__thumb-track_max"
          :style="{ width: maxThumbRightCss }"
        />
      </label>
      <div class="pl-range-slider__track" />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.pl-range-slider {
  margin: var(--pl-unit-x3) 0;
  padding: 0 14px;
  box-sizing: border-box;
  user-select: none;
  touch-action: none;

  &__inner-wrapper {
    height: var(--pl-unit-x7);
    position: relative;
    display: flex;
    align-items: center;
  }

  &__range-item_active {
    z-index: 2;
  }

  input[type="range"] {
    height: 0;
    position: absolute;
    top: 0;
    left: 0;
    appearance: none;
    width: 100%;
    background: transparent;
    border-color: transparent;
    color: transparent;
    cursor: pointer;

    &::-webkit-slider-thumb {
      appearance: none;
    }

    &::-moz-range-thumb {
      width: 1px;
      height: 1px;
      border: none;
      background: transparent;
    }

    &:focus,
    &:focus-visible {
      outline: none;
    }
  }

  &__thumb {
    position: absolute;
    width: var(--pl-unit-x7);
    height: var(--pl-unit-x7);
    border-radius: 50%;
    top: calc(50% - var(--pl-unit-x7) / 2);
    z-index: var(--pl-z-index-level1);
    background: var(--pl-rangeslider-background-dot-default);
    border: 2px solid var(--pl-rangeslider-border-dot-default);
    cursor: pointer;

    &:hover {
      background: var(--pl-rangeslider-background-dot-hover);
      border-color: var(--pl-rangeslider-border-dot-hover);
    }

    &:active {
      background: var(--pl-rangeslider-background-dot-pressed);
      border-color: var(--pl-rangeslider-border-dot-pressed);
      cursor: grab;
    }

    input[type="range"]:focus-visible + & {
      outline: 2px solid var(--pl-tabulation-border-focused);
      outline-offset: 2px;
    }
  }

  &__thumb-track {
    background-color: var(--pl-rangeslider-border-range-default);
    height: 2px;
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;

    &_min {
      left: 0;
    }

    &_max {
      right: 0;
    }
  }

  &__track {
    height: 2px;
    border-radius: var(--pl-unit-x1);
    width: 100%;
    background: var(--pl-rangeslider-border-range-active);
  }
}
</style>
